import { isNil } from './value-tools.ts'

// Utility type to merge T and S, allowing S to add/override properties in T
type Merge<T, S> = {
  [K in keyof T | keyof S]: K extends keyof S
    ? S[K] extends object
      ? K extends keyof T
        ? T[K] extends object
          ? Merge<T[K], S[K]>
          : S[K]
        : S[K]
      : S[K]
    : K extends keyof T
      ? T[K]
      : never
}

export const customMerge = <T extends object, S extends object>(
  target: T,
  source: S,
): Merge<T, S> => {
  for (const key of Object.keys(source)) {
    const sourceValue = source[key as keyof S]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const targetValue = (target as any)[key]

    if (sourceValue !== undefined && sourceValue !== null) {
      if (
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue) &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue)
      ) {
        customMerge(targetValue as object, sourceValue as object)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(target as any)[key] = sourceValue
      }
    }
  }
  return target as Merge<T, S>
}

export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    if (Object.hasOwn(obj, key)) {
      result[key] = obj[key]
    }
  }
  return result
}

export function compactObject<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => !isNil(value)),
  ) as Partial<T>
}
